import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { MailPlus, MoreHorizontal, UserMinus, UserPlus, Users } from "lucide-react";
import { useState } from "react";
import { type Member, type Role, admin } from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import { useConsole } from "../../state/state";
import {
  Empty,
  Failure,
  Field,
  FormRow,
  Hint,
  Loading,
  Mono,
  PageHeader,
  Section,
  Tag,
} from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import type { TenantPageProps } from "../pages";

// ---
// The people holding a role in this tenant
//
// One row per person, not per assignment: the table used to flatten them, so someone holding
// three roles appeared three times and read as duplicate users.
//
// The table is the page. What used to sit above it — a form for granting a role to a subject id
// typed in by hand — asked the reader to carry an identifier from a row into a field, and put the
// rarest act at the top of the most-read screen. Everything that is about one person now hangs
// off that person's row, and the two acts that are about the tenant are in the header.
// ---

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const UNITS = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
] as const;

/** "3 hours ago", in the largest unit that fits. */
function ago(iso: string, now = Date.now()): string {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return RELATIVE.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

/** What a row's menu is currently open against, and which panel it asked for. */
type Asked = { member: Member; panel: "role" } | null;

export function Members({ tenant, token, scopes }: TenantPageProps): React.JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const members = useLoad(() => admin.members(tenant, token), [tenant, token]);
  const roles = useLoad(() => admin.roles(tenant, token), [tenant, token]);
  // The tenant's projects, not the caller's. `TenantPageProps.projects` answers "which projects
  // can I mint a data-plane token for", which is empty for a pure administrator — a different
  // question from "which project may this assignment be confined to".
  const inTenant = useLoad(() => admin.projects(tenant, token), [tenant, token]);
  const action = useAction(() => members.reload());
  const [menuFor, setMenuFor] = useState<{ anchor: HTMLElement; member: Member } | null>(null);
  const [asked, setAsked] = useState<Asked>(null);
  const [offboarding, setOffboarding] = useState<Member | null>(null);
  const [roleKey, setRoleKey] = useState("");
  const [project, setProject] = useState("");
  const [sent, setSent] = useState<string | null>(null);

  // Only roles the server said this caller may delegate; offering the rest invites a 403 the
  // caller can do nothing about.
  const assignable = new Set(roles.data?.delegable ?? []);
  const humanRoles = (roles.data?.roles ?? []).filter(
    (role: Role) => role.actorType === "human" && assignable.has(role.key),
  );
  const canManage = scopes.includes("member:manage");
  const canInvite = scopes.includes("member:invite");
  const canAssign = scopes.includes("role:assign");
  // Who is signed in, from the session rather than by decoding the control token: the session
  // already says so, and a credential should not be parsed in a render path to find out. An
  // administrator removing itself is how a tenant ends up with nobody who can administer it —
  // the server refuses it too, and the item is hidden so it is never offered.
  const signedInSubject = useConsole().user?.id ?? "";
  const people = members.data ?? [];

  const nameOf = (member: Member) => member.username ?? member.email ?? member.subjectId;

  const close = () => setMenuFor(null);
  const clearForm = () => {
    setRoleKey("");
    setProject("");
  };

  return (
    <>
      <PageHeader
        title="Users"
        action={
          canInvite ? (
            <FormRow sx={{ gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<UserPlus size={15} />}
                onClick={() =>
                  void navigate({ to: "/tenants/$tenant/users-new", params: { tenant } })
                }
              >
                Add user
              </Button>
              <Button
                variant="outlined"
                startIcon={<Users size={15} />}
                onClick={() => void navigate({ to: "/tenants/$tenant/bulk", params: { tenant } })}
              >
                Bulk add users
              </Button>
            </FormRow>
          ) : undefined
        }
      />

      {sent !== null && (
        <Section title="Invitation sent">
          <Box sx={{ typography: "body1", color: tokens.ink.secondary }}>
            Mailed, if a transport is configured. The link is shown once so it can be delivered by
            hand otherwise.
          </Box>
          <Box sx={{ mt: 3 }}>
            <Mono sx={{ wordBreak: "break-all" }}>{sent}</Mono>
          </Box>
          <FormRow sx={{ mt: 4 }}>
            <Button variant="text" onClick={() => setSent(null)}>
              Done
            </Button>
          </FormRow>
        </Section>
      )}

      {asked?.panel === "role" && (
        <Section title={`Assign a role to ${nameOf(asked.member)}`}>
          <FormRow>
            <Field label="Project" sx={{ flex: "0 1 200px" }}>
              <Select
                size="small"
                value={project}
                displayEmpty
                onChange={(event) => setProject(event.target.value)}
              >
                <MenuItem value="">Tenant-wide</MenuItem>
                {(inTenant.data ?? []).map((each) => (
                  <MenuItem key={each.id} value={each.id}>
                    {each.id}
                  </MenuItem>
                ))}
              </Select>
            </Field>
            <Field label="Role" sx={{ flex: "1 1 260px", maxWidth: 320 }}>
              <Select
                size="small"
                value={roleKey}
                displayEmpty
                onChange={(event) => setRoleKey(event.target.value)}
              >
                <MenuItem value="" disabled>
                  {humanRoles.length === 0 ? "No delegable roles" : "Choose a role"}
                </MenuItem>
                {humanRoles.map((role: Role) => (
                  <MenuItem key={role.key} value={role.key}>
                    {role.key}
                  </MenuItem>
                ))}
              </Select>
            </Field>
            <Button
              variant="contained"
              disabled={roleKey === ""}
              onClick={() => {
                const subjectId = asked.member.subjectId;
                const chosen = { roleKey, ...(project === "" ? {} : { project }) };
                setAsked(null);
                clearForm();
                action.run(() => admin.assign(tenant, token, { subjectId, ...chosen }));
              }}
            >
              Assign
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setAsked(null);
                clearForm();
              }}
            >
              Cancel
            </Button>
          </FormRow>
          <Hint>
            Tenant-wide covers every project, including ones created later. Machine roles are never
            assignable to a person.
          </Hint>
          <Failure error={action.error} />
        </Section>
      )}

      {offboarding !== null && (
        <Section tone="warning" title={`Remove ${nameOf(offboarding)} from ${tenant}?`}>
          <Box sx={{ typography: "body1", color: tokens.ink.secondary }}>
            This revokes {offboarding.assignments.length}{" "}
            {offboarding.assignments.length === 1 ? "assignment" : "assignments"} here and deletes
            any invitation not yet accepted, so the link in it stops working. The account itself is
            untouched and keeps whatever it holds in other tenants — deleting the identity is a
            platform action, on the Tenants page.
          </Box>
          <FormRow sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                const subject = offboarding.subjectId;
                setOffboarding(null);
                action.run(() => admin.offboard(tenant, token, subject));
              }}
            >
              Remove from tenant
            </Button>
            <Button variant="text" onClick={() => setOffboarding(null)}>
              Cancel
            </Button>
          </FormRow>
        </Section>
      )}

      <Section flush title={`Users · ${members.data?.length ?? 0}`}>
        <Failure error={members.error} />
        {asked === null && offboarding === null && <Failure error={action.error} />}
        {members.pending ? (
          <Loading what="users" />
        ) : people.length === 0 ? (
          <Empty>No one holds a role in this tenant yet.</Empty>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Roles</TableCell>
                {/* Global: a sign-in proves who someone is, not which tenant they used. */}
                <TableCell title="Across every tenant and application">Last sign-in</TableCell>
                <TableCell align="right">Sign-ins</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {people.map((member: Member) => (
                <TableRow key={member.subjectId} hover>
                  <TableCell sx={{ color: tokens.ink.primary, verticalAlign: "top" }}>
                    {member.username ?? member.email ?? "(no account)"}
                    {member.username !== null && member.email !== null && (
                      <Box sx={{ typography: "body2", color: tokens.ink.muted }}>
                        {member.email}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Mono sx={{ fontSize: 11, color: tokens.ink.muted }}>{member.subjectId}</Mono>
                  </TableCell>
                  <TableCell>
                    {/* Each grant keeps its own Revoke. Removing one of three roles is a
                        different decision from removing the person, and folding them together
                        would make the narrower one unreachable. */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {member.assignments.map((assignment) => (
                        <Box
                          key={assignment.id}
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Mono>{assignment.roleKey}</Mono>
                          <Tag
                            label={
                              Object.keys(assignment.dimensionConstraints).length === 0
                                ? "tenant-wide"
                                : Object.entries(assignment.dimensionConstraints)
                                    .map(([key, value]) => `${key}=${value}`)
                                    .join(" ")
                            }
                          />
                          {canAssign && (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ minWidth: 0 }}
                              onClick={() =>
                                action.run(() => admin.revoke(tenant, token, assignment.id))
                              }
                            >
                              Revoke
                            </Button>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}
                    title={
                      member.lastSignInAt === null
                        ? undefined
                        : new Date(member.lastSignInAt).toLocaleString()
                    }
                  >
                    {member.lastSignInAt === null ? (
                      <Box component="span" sx={{ color: tokens.ink.muted }}>
                        Never
                      </Box>
                    ) : (
                      ago(member.lastSignInAt)
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: "top" }}>
                    {member.signInCount}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: "top" }}>
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Actions for ${nameOf(member)}`}
                      aria-haspopup="menu"
                      onClick={(event) => setMenuFor({ anchor: event.currentTarget, member })}
                      sx={{
                        appearance: "none",
                        border: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: `${tokens.radius.md}px`,
                        bgcolor: "transparent",
                        color: tokens.ink.secondary,
                        cursor: "pointer",
                        "&:hover": { bgcolor: tokens.state.hover },
                        "&:focus-visible": {
                          outline: `2px solid ${tokens.accent.base}`,
                          outlineOffset: "-2px",
                        },
                      }}
                    >
                      <MoreHorizontal size={17} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Menu
        anchorEl={menuFor?.anchor ?? null}
        open={menuFor !== null}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {canAssign && (
          <MenuItem
            sx={{ gap: 2.5 }}
            onClick={() => {
              const member = menuFor?.member;
              close();
              clearForm();
              if (member !== undefined) setAsked({ member, panel: "role" });
            }}
          >
            <UserPlus size={15} />
            Add role
          </MenuItem>
        )}
        {canInvite && (
          <MenuItem
            sx={{ gap: 2.5 }}
            onClick={() => {
              const member = menuFor?.member;
              close();
              if (member === undefined) return;
              // Offered to everyone: whether an invitation is pending is a fact only the server
              // holds, and it answers plainly when there is none.
              action.run(async () => {
                const answer = await admin.resendInvitation(tenant, token, member.subjectId);
                setSent(answer.inviteUrl);
              });
            }}
          >
            <MailPlus size={15} />
            Resend invitation
          </MenuItem>
        )}
        {canManage &&
          menuFor?.member.subjectId !== signedInSubject && [
            <Divider key="rule" sx={{ my: 1 }} />,
            <MenuItem
              key="offboard"
              sx={{ gap: 2.5, color: tokens.semantic.dangerInk }}
              onClick={() => {
                const member = menuFor?.member;
                close();
                if (member !== undefined) setOffboarding(member);
              }}
            >
              <UserMinus size={15} />
              Revoke from tenant
            </MenuItem>,
          ]}
      </Menu>
    </>
  );
}
